import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Pencil } from 'lucide-react';
import { Dialog, DialogClose, DialogContent, DialogFooter, DialogTrigger } from './ui/Dialog';
import { Button } from './ui/Button';
import { Alert } from './ui/Alert';
import { Input } from './ui/Input';
import { Label } from './ui/Label';
import { apiFetch } from '../lib/api';
import { useAuth } from '../lib/auth';

const MAX_LENGTH = 60;

/** The logged-in user's own name in the admin (sidebar, Overview greeting). Blank = the name guessed from the email. */
export function ChangeNameDialog({ current }: { current: string }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(current);
  const { refreshMe } = useAuth();

  const mutation = useMutation({
    mutationFn: () =>
      apiFetch<{ displayName: string | null }>('/auth/me', {
        method: 'PATCH',
        body: { displayName: name.trim() || null },
      }),
    onSuccess: async () => {
      await refreshMe();
      setOpen(false);
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) setName(current);
        else mutation.reset();
      }}
    >
      <DialogTrigger asChild>
        <button
          type="button"
          aria-label="Change name"
          title="Change name"
          className="grid h-8 w-8 flex-none place-items-center rounded-[7px] text-muted hover:bg-border-soft hover:text-text"
        >
          <Pencil size={15} aria-hidden="true" />
        </button>
      </DialogTrigger>
      <DialogContent
        title="Change name"
        description="Shown in the menu and the Overview greeting. Leave it blank to use the name from your email."
      >
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (!mutation.isPending) mutation.mutate();
          }}
        >
          {mutation.isError ? <Alert>The name could not be saved. Try again.</Alert> : null}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="display-name">Name</Label>
            <Input
              id="display-name"
              value={name}
              maxLength={MAX_LENGTH}
              autoComplete="off"
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <DialogFooter>
            <DialogClose>
              <Button type="button" variant="secondary" className="h-11">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" variant="primary" disabled={mutation.isPending} className="h-11">
              {mutation.isPending ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
